import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { adminStatsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { ArrowPathIcon, DocumentTextIcon, ServerStackIcon } from '@heroicons/react/24/outline';

const AdminStatsPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { data, loading, error, refetch } = useApi(
    () => adminStatsAPI.getUploadStorage(),
    [],
    { showErrorToast: true, immediate: true }
  );

  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/dashboard';
    }
  }, [isAdmin]);

  const otherModules = data?.modules
    ? Object.entries(data.modules).filter(([name]) => name !== 'ddp-documents')
    : [];

  const quota = data?.quota;
  const quotaLimitBytes = quota?.limitMb != null ? quota.limitMb * 1024 * 1024 : 0;
  const quotaPct =
    quota && quotaLimitBytes > 0
      ? Math.min(100, Math.round((quota.usedBytes / quotaLimitBytes) * 1000) / 10)
      : 0;
  const quotaNearLimit = quotaPct >= 85 && quotaPct < 100;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminStats.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('adminStats.subtitle')}</p>
        </div>
        <div className="mt-4 md:mt-0">
          <Button type="button" variant="secondary" onClick={() => refetch()} disabled={loading}>
            <ArrowPathIcon className={`h-4 w-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            {t('adminStats.refresh')}
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {error && !data && (
        <p className="text-red-600 text-sm">{error}</p>
      )}

      {data && (
        <>
          {quota && (
            <div className="mb-8 bg-white shadow rounded-lg p-6 border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('adminStats.quotaTitle')}</h2>
              <p className="text-sm text-gray-600 mb-3">
                {t('adminStats.quotaUsage', { used: quota.usedMb, limit: quota.limitMb })}
              </p>
              <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    quotaPct >= 100 ? 'bg-red-500' : quotaPct >= 85 ? 'bg-amber-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${quotaPct}%` }}
                />
              </div>
              {quotaNearLimit && (
                <p className="text-sm text-amber-700 mt-3">{t('adminStats.quotaNearLimit')}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white shadow rounded-lg p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">{t('adminStats.ddpDocumentsTitle')}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">{t('adminStats.ddpDocumentsDesc')}</p>
              <p className="text-3xl font-bold text-gray-900 tabular-nums">
                {data.ddpDocuments?.mb ?? 0} <span className="text-lg font-medium text-gray-500">MB</span>
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t('adminStats.fileCount', { count: data.ddpDocuments?.fileCount ?? 0 })}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <ServerStackIcon className="h-8 w-8 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">{t('adminStats.allUploadsTitle')}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">{t('adminStats.allUploadsDesc')}</p>
              <p className="text-3xl font-bold text-gray-900 tabular-nums">
                {data.total?.mb ?? 0} <span className="text-lg font-medium text-gray-500">MB</span>
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t('adminStats.fileCount', { count: data.total?.fileCount ?? 0 })}
              </p>
            </div>
          </div>

          {otherModules.length > 0 && (
            <div className="bg-white shadow rounded-lg border border-gray-100 overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">{t('adminStats.otherFolders')}</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminStats.folderName')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      MB
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminStats.files')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {otherModules.map(([name, stats]) => (
                    <tr key={name}>
                      <td className="px-6 py-3 text-sm text-gray-900 font-mono">{name}</td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right tabular-nums">{stats.mb}</td>
                      <td className="px-6 py-3 text-sm text-gray-600 text-right tabular-nums">{stats.fileCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-sm text-gray-500 max-w-3xl">{t('adminStats.noteImports')}</p>
        </>
      )}
    </div>
  );
};

export default AdminStatsPage;
